import { CloseOutlined } from '@ant-design/icons';
import { type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/Button';

import {
  CornerHandle,
  FloatingCardFrame,
  FloatingContainer,
  FloatingContent,
  FloatingHeader,
  FloatingHeaderTitle,
} from './FloatingCard.style';
import {
  type AnchorPlacement,
  type FloatingCardPosition,
  type FloatingCardSize,
  type FloatingCardState,
  type ResizeCorner,
  useFloatingCard,
} from './useFloatingCard';

export type FloatingCardShellProps = {
  title: string;
  icon?: ReactNode;
  headerActions?: ReactNode;
  onClose: () => void;
  closeTitle?: string;
  usePortal?: boolean;
  zIndex?: number;
  position: FloatingCardPosition;
  size: FloatingCardSize;
  /** Scales header typography and spacing; content can read `--floating-card-scale` (defaults to 1). */
  scale?: number;
  headerRef: RefObject<HTMLDivElement>;
  onHeaderMouseDown: (e: React.MouseEvent) => void;
  onResizeMouseDown: (e: React.MouseEvent, corner: ResizeCorner) => void;
  resizeCursors: Record<ResizeCorner, string>;
  getCornerHandleProps: (corner: ResizeCorner) => Record<string, number | undefined>;
  children: ReactNode;
  testId?: string;
};

export const FloatingCardShell = ({
  title,
  icon,
  headerActions,
  onClose,
  closeTitle,
  usePortal = false,
  zIndex,
  position,
  size,
  scale,
  headerRef,
  onHeaderMouseDown,
  onResizeMouseDown,
  resizeCursors,
  getCornerHandleProps,
  children,
  testId,
}: FloatingCardShellProps) => {
  const { t } = useTranslation();
  const card = (
    <FloatingContainer
      $left={position.x}
      $top={position.y}
      $width={size.width}
      $height={size.height}
      $zIndex={zIndex}
      data-testid={testId}
    >
      <FloatingCardFrame
        style={
          scale === undefined
            ? undefined
            : ({ '--floating-card-scale': scale } as React.CSSProperties)
        }
      >
        <FloatingHeader ref={headerRef} onMouseDown={onHeaderMouseDown}>
          {icon}
          <FloatingHeaderTitle>{title}</FloatingHeaderTitle>
          {headerActions}
          <Button
            size="small"
            icon={<CloseOutlined />}
            title={closeTitle ?? t('common.close', 'Close')}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              onClose();
            }}
            onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          />
        </FloatingHeader>
        <FloatingContent>{children}</FloatingContent>
      </FloatingCardFrame>

      {(['se', 'sw', 'ne', 'nw'] as ResizeCorner[]).map(corner => (
        <CornerHandle
          key={corner}
          $cursor={resizeCursors[corner]}
          {...getCornerHandleProps(corner)}
          onMouseDown={e => onResizeMouseDown(e, corner)}
        />
      ))}
    </FloatingContainer>
  );

  return usePortal ? createPortal(card, document.body) : card;
};

export type FloatingCardProps = {
  title: string;
  icon?: ReactNode;
  headerActions?: ReactNode;
  onClose: () => void;
  closeTitle?: string;
  minSize: FloatingCardSize;
  initialSize: FloatingCardSize;
  initialPosition?: FloatingCardPosition;
  initialState?: FloatingCardState;
  anchorRef?: RefObject<HTMLElement | null>;
  anchorPlacement?: AnchorPlacement;
  onUnmount?: (state: FloatingCardState) => void;
  usePortal?: boolean;
  zIndex?: number;
  children: ReactNode;
  testId?: string;
};

export const FloatingCard = ({
  title,
  icon,
  headerActions,
  onClose,
  closeTitle,
  minSize,
  initialSize,
  initialPosition,
  initialState,
  anchorRef,
  anchorPlacement,
  onUnmount,
  usePortal,
  zIndex,
  children,
  testId,
}: FloatingCardProps) => {
  const floatingCard = useFloatingCard({
    minSize,
    initialSize,
    initialPosition,
    initialState,
    anchorRef,
    anchorPlacement,
    onUnmount,
  });

  const {
    position,
    size,
    headerRef,
    isAnchorPositioned,
    onHeaderMouseDown,
    onResizeMouseDown,
    resizeCursors,
    getCornerHandleProps,
  } = floatingCard;

  if (anchorRef && !isAnchorPositioned) {
    return null;
  }

  return (
    <FloatingCardShell
      title={title}
      icon={icon}
      headerActions={headerActions}
      onClose={onClose}
      closeTitle={closeTitle}
      usePortal={usePortal}
      zIndex={zIndex}
      position={position}
      size={size}
      headerRef={headerRef}
      onHeaderMouseDown={onHeaderMouseDown}
      onResizeMouseDown={onResizeMouseDown}
      resizeCursors={resizeCursors}
      getCornerHandleProps={getCornerHandleProps}
      testId={testId}
    >
      {children}
    </FloatingCardShell>
  );
};
